// Brand Controls redesign — shared primitives.
//
// Presentational-only building blocks used across the Overview / Quick
// Setup / Advanced / History views. No service calls, no data ownership —
// everything flows in via props from the orchestrator.

import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

/** Section eyebrow — "0N — Title" uppercase muted label. */
export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: ".14em",
        textTransform: "uppercase",
        color: "var(--muted-foreground)",
      }}
    >
      {children}
    </div>
  );
}

/** The violet AI orb (Sparkles). Breathing glow via .bc-orb. */
export function Orb({ size = 22, icon = 12 }: { size?: number; icon?: number }) {
  return (
    <span
      className="bc-orb"
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size / 3),
        background: "var(--orb-gradient)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        flexShrink: 0,
      }}
    >
      <Sparkles style={{ width: icon, height: icon }} strokeWidth={1.7} />
    </span>
  );
}

/**
 * ConfidenceRing — the data-viz signature. Animates 0 -> pct on mount.
 * Colour by threshold: >=80 green, >=60 blue, else coral. Reduced-motion
 * users get the final value with no transition (mount flips immediately).
 */
export function ConfidenceRing({ pct, size = 34 }: { pct: number; size?: number }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => setMounted(true))
    );
    return () => cancelAnimationFrame(raf);
  }, []);

  const clamped = Math.max(0, Math.min(100, Math.round(pct || 0)));
  const r = (size - 5) / 2;
  const c = 2 * Math.PI * r;
  const col =
    clamped >= 80
      ? "var(--signal-green)"
      : clamped >= 60
      ? "var(--signal-blue)"
      : "var(--infrared-coral)";

  return (
    <div
      style={{ position: "relative", width: size, height: size, flexShrink: 0 }}
      title={`AI confidence: ${clamped}%`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: "rotate(-90deg)" }}
      >
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--muted)" strokeWidth={3} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={col}
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={mounted ? c * (1 - clamped / 100) : c}
          style={{ transition: "stroke-dashoffset .9s var(--ease-standard)" }}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: size >= 34 ? "10px" : "9px",
          fontWeight: 700,
          color: "var(--foreground)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {clamped}
      </div>
    </div>
  );
}

/**
 * EmptyState — ONE shared treatment for Overview / Advanced / History when
 * no profile exists. Only the copy differs (headline + body via props).
 */
export function EmptyState({
  headline,
  body,
  onExtract,
}: {
  headline: string;
  body: string;
  onExtract: () => void;
}) {
  return (
    <div
      style={{
        border: "1px dashed var(--silver-mist)",
        borderRadius: 20,
        background:
          "radial-gradient(140% 120% at 0% 0%, var(--spectral-violet-10), transparent 46%), radial-gradient(130% 130% at 100% 100%, var(--alpha-teal-10), transparent 48%), var(--muted)",
        padding: "clamp(40px,7vw,72px) 32px",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <span
        className="bc-orb"
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: "var(--orb-gradient)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          marginBottom: 20,
        }}
      >
        <Sparkles style={{ width: 26, height: 26 }} strokeWidth={1.6} />
      </span>
      <h2
        style={{
          fontSize: "clamp(20px,3vw,26px)",
          fontWeight: 600,
          letterSpacing: "-.01em",
          color: "var(--foreground)",
          margin: "0 0 8px",
          maxWidth: "22ch",
          textWrap: "balance",
        }}
      >
        {headline}
      </h2>
      <p
        style={{
          fontSize: "15px",
          color: "var(--muted-foreground)",
          lineHeight: 1.55,
          margin: "0 0 22px",
          maxWidth: 440,
          textWrap: "pretty",
        }}
      >
        {body}
      </p>
      <button
        onClick={onExtract}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 14,
          fontWeight: 600,
          color: "#fff",
          background: "var(--orb-gradient)",
          border: "none",
          borderRadius: 11,
          padding: "11px 20px",
          cursor: "pointer",
          boxShadow: "var(--shadow-spectral-violet-glow)",
        }}
      >
        <Sparkles style={{ width: 15, height: 15 }} strokeWidth={1.8} />
        Extract my voice
      </button>
    </div>
  );
}

/** An extracted-attribute card: label + optional confidence ring + body. */
export function AttrCard({
  label,
  ring,
  compact = false,
  children,
}: {
  label: string;
  ring?: number;
  compact?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: compact ? 12 : 14,
        padding: compact ? 16 : 18,
        background: "var(--card)",
        boxShadow: compact ? undefined : "var(--elevation-sm)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: compact ? 12 : 13 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>{label}</span>
        {typeof ring === "number" && <ConfidenceRing pct={ring} />}
      </div>
      <div>{children}</div>
    </div>
  );
}

/** A wrapping row of muted pill chips. */
export function ChipRow({ items }: { items: string[] }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {items.map((t, i) => (
        <span
          key={i}
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "var(--foreground)",
            background: "var(--muted)",
            border: "1px solid var(--border)",
            borderRadius: 9999,
            padding: "3px 10px",
          }}
        >
          {t}
        </span>
      ))}
    </div>
  );
}

/**
 * The 4 extracted-attribute cards (Tone / Readability / Sentence length /
 * Common phrases) shared by Overview §04 and Quick Setup's done state.
 * Confidence rings bind to GeneratedAttributes; Common phrases has no
 * confidence in the data, so it renders without a ring.
 */
export function ExtractedAttrCards({
  attrs,
  compact = false,
}: {
  attrs: {
    tone?: { detected?: string[]; confidence?: number };
    readabilityLevel?: { grade?: string; fleschScore?: number; confidence?: number };
    sentenceLength?: { avg?: number; range?: string; confidence?: number };
    commonPhrases?: string[];
  } | null;
  compact?: boolean;
}) {
  if (!attrs) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit,minmax(240px,1fr))`, gap: 14 }}>
      <AttrCard label="Tone" ring={attrs.tone?.confidence} compact={compact}>
        <ChipRow items={attrs.tone?.detected || []} />
      </AttrCard>
      <AttrCard label="Readability" ring={attrs.readabilityLevel?.confidence} compact={compact}>
        <span style={{ fontSize: 14, color: "var(--foreground)" }}>
          Grade {attrs.readabilityLevel?.grade} · Flesch {attrs.readabilityLevel?.fleschScore}
        </span>
      </AttrCard>
      <AttrCard label="Sentence length" ring={attrs.sentenceLength?.confidence} compact={compact}>
        <span style={{ fontSize: 14, color: "var(--foreground)" }}>
          Avg {attrs.sentenceLength?.avg} words · range {attrs.sentenceLength?.range}
        </span>
      </AttrCard>
      <AttrCard label="Common phrases" compact={compact}>
        <ChipRow items={(attrs.commonPhrases || []).map((p) => `"${p}"`)} />
      </AttrCard>
    </div>
  );
}

/** Small teal chevron-arrow used in "steers around" strips. */
export function ArrowRight({ size = 14, color = "var(--muted-foreground)" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
